import type { ApiClient } from '../api/client';
import { uploadFile } from './upload';
import type { AttachmentRef } from './upload';

type UploadApi = Pick<ApiClient, 'createUpload' | 'putBlob'>;

/**
 * Uploads the leading capture while the reporter is still writing, so Send no
 * longer waits on it (I24294, ADR 0045).
 *
 * A full-page capture is a lossless PNG at the device pixel ratio, so it is
 * megabytes: 6.3 MB for a 1440x4712 page at 2x. Uploaded at Send, that PUT was
 * nearly the whole wait between the click and the confirmation (5.3 s of 5.9 s
 * on a 10 Mbit/s uplink). Starting it the moment the capture lands spends the
 * time the reporter is typing instead, and keeps every byte of the image.
 *
 * Keyed by the `Blob` itself, because the capture's identity IS its bytes: a
 * retake or a manual capture is a new blob and starts its own upload, while
 * dropping a marker changes only the pins, which travel on the request and
 * never re-upload. A blob that is uploaded and then never sent (removed,
 * retaken, the dialog closed) names no row, so nothing can mint a read URL for
 * it, and the orphan sweep deletes it once it is past its grace window.
 */
export interface CaptureUploads {
  /** Begins uploading `blob` now unless it is already uploading. Never throws or rejects. */
  start(blob: Blob): void;
  /**
   * The reference for `blob`, reusing an upload `start` already began. A
   * started upload that failed is retried once here rather than failing the
   * send, because it failed before the reporter asked for anything.
   */
  get(blob: Blob): Promise<AttachmentRef>;
}

/** The file a capture travels as. Named at upload time, which is now when it lands. */
export function captureFile(blob: Blob): File {
  return new File([blob], `screenshot-${Date.now()}.png`, {
    type: blob.type || 'image/png',
  });
}

export function createCaptureUploads(api: UploadApi): CaptureUploads {
  const started = new WeakMap<Blob, Promise<AttachmentRef>>();

  const upload = (blob: Blob): Promise<AttachmentRef> => {
    const pending = uploadFile(api, captureFile(blob), 'SCREENSHOT');
    started.set(blob, pending);
    // Forget a failed attempt so the next `get` uploads afresh instead of
    // handing back the same rejection, and so an unawaited early start never
    // surfaces as an unhandled rejection on the host page.
    pending.catch(() => {
      if (started.get(blob) === pending) started.delete(blob);
    });
    return pending;
  };

  return {
    start(blob) {
      if (started.has(blob)) return;
      try {
        upload(blob);
      } catch {
        // A synchronous throw from a misbehaving client must not reach the
        // modal's render; `get` will try again at Send.
      }
    },

    get(blob) {
      const pending = started.get(blob);
      if (!pending) return upload(blob);
      return pending.catch(() => upload(blob));
    },
  };
}
